import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import { Box, List, ListItem, ListItemText, ListItemIcon, Divider } from '@mui/material';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

// Icons
import LoginIcon from '@mui/icons-material/Login';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import InfoIcon from '@mui/icons-material/Info';
import ChatIcon from '@mui/icons-material/Chat';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';

const Sidebar: React.FC = () => {
  const { isAuthenticated, currentUser, logout } = useAuth();
  const [kb2Enabled, setKb2Enabled] = useState(false);
  useEffect(() => {
    let active = true;
    api.get('/llm/chat-kb2/config').then(r => { if (active) setKb2Enabled(r.data.enabled); }).catch(() => { if (active) setKb2Enabled(false); });
    return () => { active = false; };
  }, []);
  const location = useLocation();
  const navigate = useNavigate();
  
  const isActive = (path: string) => location.pathname === path;
  
  const handleLogout = () => {
    logout();
    navigate('/login');
  };
  
  const handleNavigation = (path: string) => {
    if (isAuthenticated) {
      navigate(path);
    } else {
      // Redirect to login with a returnUrl
      navigate(`/login?returnUrl=${path}`, { 
        state: { 
          message: "Please log in to access this feature",
          severity: "info"
        } 
      });
    }
  };
  
  return (
    <Box sx={{ py: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ px: 2, mb: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Link to="/">
          <Box 
            component="img" 
            src="/images/EcoActionAI_WhiteBlueRev_RGB.png"
            alt="EcoActionAI Logo" 
            sx={{ 
              width: '80%', 
              maxWidth: 180,
              mx: 'auto',
              mb: 2,
              display: 'block'
            }}
          />
        </Link>
      </Box>
      
      <List sx={{ width: '100%' }}>
        {!isAuthenticated ? (
          <>
            <ListItem 
              component={Link} 
              to="/login"
              className={`nav-item ${isActive('/login') ? 'active' : ''}`}
              sx={{
                borderRadius: '4px',
                my: 0.5,
                px: 2,
                py: 1
              }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: 'text.primary' }}>
                <LoginIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText 
                primary="Login" 
                primaryTypographyProps={{ 
                  fontSize: '0.9rem',
                  fontWeight: isActive('/login') ? 600 : 400
                }} 
              />
            </ListItem>
            
            <ListItem 
              component={Link} 
              to="/signup"
              className={`nav-item ${isActive('/signup') ? 'active' : ''}`}
              sx={{
                borderRadius: '4px',
                my: 0.5,
                px: 2,
                py: 1
              }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: 'white' }}>
                <PersonAddIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText 
                primary="Sign Up" 
                primaryTypographyProps={{ 
                  fontSize: '0.9rem',
                  fontWeight: isActive('/signup') ? 600 : 400
                }} 
              />
            </ListItem>
            
            <ListItem 
              component={Link} 
              to="/forgot-password"
              className={`nav-item ${isActive('/forgot-password') ? 'active' : ''}`}
              sx={{
                borderRadius: '4px',
                my: 0.5,
                px: 2,
                py: 1
              }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: 'white' }}>
                <HelpOutlineIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText 
                primary="Forgot Password" 
                primaryTypographyProps={{ 
                  fontSize: '0.9rem',
                  fontWeight: isActive('/forgot-password') ? 600 : 400
                }} 
              />
            </ListItem>
            
            <Divider sx={{ my: 2, backgroundColor: 'rgba(255,255,255,0.1)' }} />
          </>
        ) : null}
        
        <ListItem 
          component={Link} 
          to="/"
          className={`nav-item ${isActive('/') ? 'active' : ''}`}
          sx={{
            borderRadius: '4px',
            my: 0.5,
            px: 2,
            py: 1
          }}
        >
          <ListItemIcon sx={{ minWidth: 40, color: 'white' }}>
            <InfoIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText 
            primary="About" 
            primaryTypographyProps={{ 
              fontSize: '0.9rem',
              fontWeight: isActive('/') ? 600 : 400
            }} 
          />
        </ListItem>
        
        {kb2Enabled && <ListItem onClick={() => handleNavigation('/chat-kb2')} className={`nav-item ${isActive('/chat-kb2') ? 'active' : ''}`} sx={{ cursor: 'pointer', px: 2, py: 1, opacity: isAuthenticated ? 1 : 0.5 }}>
          <ListItemIcon sx={{ minWidth: 40, color: 'white' }}><ChatIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Chat" secondary="Curated guidance for climate reporting and action" />
        </ListItem>}

        {isAuthenticated && (
          <>
            <Divider sx={{ my: 2, backgroundColor: 'rgba(255,255,255,0.1)' }} />
            
            <ListItem 
              component={Link} 
              to="/account"
              className={`nav-item ${isActive('/account') ? 'active' : ''}`}
              sx={{
                borderRadius: '4px',
                my: 0.5,
                px: 2,
                py: 1
              }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: 'white' }}>
                <AccountCircleIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText 
                primary={currentUser?.first_name || "Account"} 
                primaryTypographyProps={{ 
                  fontSize: '0.9rem',
                  fontWeight: isActive('/account') ? 600 : 400
                }}
              />
            </ListItem>
            
            {currentUser?.is_admin && (
              <ListItem 
                component={Link} 
                to="/admin"
                className={`nav-item ${isActive('/admin') ? 'active' : ''}`}
                sx={{
                  borderRadius: '4px',
                  my: 0.5,
                  px: 2,
                  py: 1
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: 'white' }}>
                  <AdminPanelSettingsIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText 
                  primary="Admin Panel" 
                  primaryTypographyProps={{ 
                    fontSize: '0.9rem',
                    fontWeight: isActive('/admin') ? 600 : 400
                  }}
                />
              </ListItem>
            )}
            
            <ListItem 
              onClick={handleLogout}
              className="nav-item"
              sx={{ 
                cursor: 'pointer',
                borderRadius: '4px',
                my: 0.5,
                px: 2,
                py: 1
              }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: 'white' }}>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText 
                primary="Logout" 
                primaryTypographyProps={{ fontSize: '0.9rem' }}
              />
            </ListItem>
          </>
        )}
      </List>
      
      <Box sx={{ mt: 'auto', px: 2, pt: 2, textAlign: 'center' }}>
        <Box sx={{ mb: 2 }}>
          <Link to="/">
            <Box 
              component="img" 
              src="/images/CSIRO_Logo.svg.png"
              alt="CSIRO Logo" 
              sx={{ 
                width: '50%', 
                maxWidth: 80,
                mx: 'auto',
                mb: 2,
                display: 'block'
              }}
            />
          </Link>
          <Box sx={{ fontSize: '0.8rem', color: 'rgba(255, 255, 255, 0.7)', mb: 1 }}>
            EcoActionAI © 2025 CSIRO
          </Box>
          <Box sx={{ fontSize: '0.7rem', color: 'rgba(255, 255, 255, 0.7)' }}>
            <Link to="/" style={{ color: 'rgba(255, 255, 255, 0.7)', textDecoration: 'none' }} 
                  onClick={(e: React.MouseEvent) => { e.preventDefault(); window.location.href = 'mailto:supports-ecoactionai@csiro.au'; }}>
              Need help? Contact supports-ecoactionai@csiro.au
            </Link>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Sidebar;
